<script>
	//@ts-nocheck
	import { fly } from 'svelte/transition';
	import { quintOut } from 'svelte/easing';
	import { toasts, removeToast } from '$lib/utils.svelte.js';
	import JSConfetti from 'js-confetti';
	import { onMount } from 'svelte';
	import { t } from '$lib/i18n.svelte.js';

	let jsConfetti = $state();

	onMount(() => (jsConfetti = new JSConfetti()));

	/**
	 * @param {string} content
	 * @returns {string}
	 */
	function processContent(content) {
		return content.replace(/\[\[([^\]]+)\]\]/g, (_, match) => {
			return `<span class="increment-number inline-block" data-value="${match}">${match}</span>`;
		});
	}

	/**
	 * @param {HTMLElement} node
	 * @param {{ duration: number }} options
	 */
	function incrementNumber(node, { duration }) {
		const targetValue = parseInt(node.dataset.value);
		const prefix =
			node.dataset.value.startsWith('+') || node.dataset.value.startsWith('-')
				? node.dataset.value[0]
				: '';
		const increment = targetValue / (duration / 20);
		let currentValue = 0;

		const interval = setInterval(() => {
			currentValue += increment;
			node.textContent = prefix + Math.floor(Math.abs(currentValue));

			if (Math.abs(currentValue) >= Math.abs(targetValue)) {
				node.textContent = node.dataset.value;
				clearInterval(interval);
			}
		}, 20);

		return {
			destroy() {
				clearInterval(interval);
			}
		};
	}

	/**
	 * @param {{ type: string; }} toast
	 */
	function triggerConfetti(toast) {
		if (toast.type === 'party') {
			jsConfetti.addConfetti({
				emojis: ['🎉', '✨', '🌟'],
				emojiSize: 30,
				confettiNumber: 30
			});
		} else if (toast.type == 'partyhard') {
			jsConfetti.addConfetti({
				emojis: ['🎉', '✨', '🌟'],
				emojiSize: 36,
				confettiNumber: 80
			});
		}
	}

	/**
	 * @param {{ id: string; onClick?: Function; }} toast
	 */
	function handleToastClick(toast) {
		if (toast.onClick) {
			toast.onClick();
		}
		removeToast(toast.id);
	}

	const getPositionClass = (position) => {
		switch (position) {
			case 'bottom-center':
				return 'bottom-0 left-1/2 -translate-x-1/2 mb-4 items-center';
			case 'top-right':
				return 'top-0 right-0 m-4 items-end';
			case 'top-center':
				return 'top-0 left-1/2 -translate-x-1/2 mt-4 items-center';
			case 'top-left':
				return 'top-0 left-0 m-4 items-start';
			case 'bottom-left':
				return 'bottom-0 left-0 m-4 items-start';
			case 'bottom-right':
			default:
				return 'bottom-0 right-0 m-4 items-end';
		}
	};
</script>

<svelte:window onkeydown={(event) => {
	if (event.key === 'Escape' && document.getElementById('text-editor') == null) {
		const visibleToasts = $toasts.filter(t => t.isLoading !== true);
		if (visibleToasts.length > 0) {
			removeToast(visibleToasts[0].id);
		}
	}
}} />

{#each ['bottom-right', 'bottom-center', 'top-right', 'top-center', 'top-left', 'bottom-left'] as position}
	<div
		style="z-index: 999999;"
		class="fixed {getPositionClass(position)} space-y-2.5 z-50 flex flex-col"
	>
		{#each $toasts.filter(toast => (toast.position || 'bottom-right') === position) as toast (toast.id)}
			<!-- svelte-ignore a11y_click_events_have_key_events -->
			<!-- svelte-ignore a11y_no_static_element_interactions -->
			<div
				class="toast relative w-fit max-w-lg rounded-xl shadow-md backdrop-blur-sm
					   transition-all duration-200 ease-out {toast.isLoading ? '' : 'hover:translate-y-[1px] hover:shadow-md cursor-pointer'} 
					   {toast.type || 'neutral'} {toast.dark ? 'dark' : ''} flex items-center gap-3 px-4 py-3 text-sm font-medium"
				in:fly={{ y: position.startsWith('top') ? -100 : 100, duration: 300, easing: quintOut }}
				out:fly={{ y: position.startsWith('top') ? -10 : 10, duration: 250 }}
				onintroend={() => triggerConfetti(toast)}
				onclick={() => !toast.isLoading && handleToastClick(toast)}
			>
				<!-- Type indicator or loading spinner -->
				{#if toast.isLoading}
					<div class="loading-spinner shrink-0"></div>
				{:else if toast.type === 'success'}
					<svg class="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
						<path d="M20 6L9 17l-5-5" stroke-linecap="round" stroke-linejoin="round" />
					</svg>
				{:else if toast.type === 'error'}
					<svg class="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
						<circle cx="12" cy="12" r="10" />
						<path d="M12 8v4" stroke-linecap="round" stroke-linejoin="round" />
						<path d="M12 16h.01" stroke-linecap="round" stroke-linejoin="round" />
					</svg>
				{:else if toast.type === 'party' || toast.type === 'partyhard'}
					<svg class="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
						<path d="M12 2v2m0 16v2M4 12H2m20 0h-2m-.364-7.364l-1.414 1.414m-14.142 14.142l-1.414 1.414m1.414-16.97l-1.414-1.414m14.142 14.142l1.414 1.414" stroke-linecap="round" stroke-linejoin="round" />
						<circle cx="12" cy="12" r="4" />
					</svg>
				{/if}
				<div class="flex-1 min-w-0">
					<div class="break-words">
						{@html processContent(toast.message)}
					</div>
					{#if toast.onClick && !toast.isLoading}
						<div class="text-xs opacity-60 mt-1 font-normal">{t('info.clickToAccess')}</div>
					{/if}
				</div>
				{#if !toast.isLoading}
					<button
						class="rounded-full p-1.5 transition-all duration-150 hover:bg-black/10 dark:hover:bg-white/10 opacity-25 hover:opacity-70 shrink-0 self-start mt-0.5"
						onclick={(e) => {
							e.stopPropagation();
							removeToast(toast.id);
						}}
						aria-label="Close notification"
					>
						<svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
							<path d="M18 6L6 18M6 6l12 12" stroke-linecap="round" stroke-linejoin="round" />
						</svg>
					</button>
				{/if}
			</div>
		{/each}
	</div>
{/each}

<style>
	/* Base toast styles with subtle modern look */
	.toast {
		border: 1px solid rgba(0, 0, 0, 0.08);
		position: relative;
		overflow: hidden;
		line-height: 1.5;
		letter-spacing: -0.01em;
	}
	
	/* Light theme toast styles - clean and minimalist with very subtle distinctions */
	.success {
		background-color: rgba(255, 255, 255, 0.98);
		color: #1a7f37;
		box-shadow: 0 1px 6px rgba(26, 127, 55, 0.04);
	}
	
	.error {
		background-color: rgba(255, 255, 255, 0.98);
		color: #cf222e;
		box-shadow: 0 1px 6px rgba(207, 34, 46, 0.04);
	}
	
	.neutral {
		background-color: rgba(255, 255, 255, 0.98);
		color: #24292f;
		box-shadow: 0 1px 6px rgba(36, 41, 47, 0.04);
	}
	
	.party, .partyhard {
		background-color: rgba(255, 255, 255, 0.98);
		color: #0969da;
		box-shadow: 0 1px 6px rgba(9, 105, 218, 0.04);
	}
	
	.loading {
		background-color: rgba(255, 255, 255, 0.98);
		color: #24292f;
		box-shadow: 0 1px 6px rgba(36, 41, 47, 0.04);
	}
	
	/* Dark theme toast styles - clean and minimalist with very subtle distinctions */
	.success.dark {
		background-color: rgba(30, 30, 30, 0.98);
		color: #7ee787;
		box-shadow: 0 1px 6px rgba(126, 231, 135, 0.03);
	}
	
	.error.dark {
		background-color: rgba(30, 30, 30, 0.98);
		color: #ff7b72;
		box-shadow: 0 1px 6px rgba(255, 123, 114, 0.03);
	}
	
	.neutral.dark {
		background-color: rgba(30, 30, 30, 0.98);
		color: #e6edf3;
		box-shadow: 0 1px 6px rgba(230, 237, 243, 0.02);
	}
	
	.party.dark, .partyhard.dark {
		background-color: rgba(30, 30, 30, 0.98);
		color: #a5d6ff;
		box-shadow: 0 1px 6px rgba(165, 214, 255, 0.03);
	}
	
	.loading.dark {
		background-color: rgba(30, 30, 30, 0.98);
		color: #e6edf3;
		box-shadow: 0 1px 6px rgba(230, 237, 243, 0.02);
	}
	
	:global(.increment-number) {
		display: inline;
	}
	
	.loading-spinner {
		border: 2px solid rgba(128, 128, 128, 0.08);
		border-radius: 50%;
		border-top: 2px solid currentColor;
		width: 16px;
		height: 16px;
		animation: spin 0.8s cubic-bezier(0.4, 0, 0.2, 1) infinite;
	}
	
	@keyframes spin {
		0% { transform: rotate(0deg); }
		100% { transform: rotate(360deg); }
	}
	
	/* Hover and active states refinements */
	.toast:not(.loading):hover {
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
	}
	
	.toast:not(.loading):active {
		transform: translateY(1px);
		transition-duration: 0.1s;
	}
	
	/* Icon and spinner styles */
	.loading-spinner {
		margin-left: 1px;
		margin-right: 1px;
	}
	
	svg {
		opacity: 0.9;
	}
</style>
